import axios from '@axios'
import { tr } from 'date-fns/locale'
import store from '@/store'

export default {
  namespaced: true,
  state: {
    items: [],
    panelConfig: null,
    isOperationActive: false,
    areasList: [],
    // Track sync status per tab: 'init' | 'syncing' | 'synced' | 'error'
    tabSyncStatus: {},
  },
  getters: {
    getItems(state) {
      return state.items
    },
    getSelectedItems(state) {
      return state.items.map(panel => ({
        value: panel.id,
        label: panel.name,
      }))
    },
    getPanelConfig(state) {
      return state.panelConfig
    },
    isOperationActive(state) {
      return state.isOperationActive
    },
    getAreasList(state) {
      return state.areasList.map(area => ({
        id: area.id,
        name: area.name,
      }))
    },
    getTabSyncStatus: (state) => (tabIndex) => {
      return state.tabSyncStatus[tabIndex] || 'init'
    }
  },
  mutations: {
    setData(state, value) {
      state.items = value
    },
    setPanelConfig(state, value) {
      state.panelConfig = value
    },
    clearPanelConfig(state) {
      state.panelConfig = null
    },
    setOperationActive(state, value) {
      state.isOperationActive = value
    },
    setAreasList(state, value) {
      state.areasList = value
    },
    setTabSyncStatus(state, { tabIndex, status }) {
      state.tabSyncStatus = { ...state.tabSyncStatus, [tabIndex]: status }
    },
    resetTabSyncStatus(state) {
      state.tabSyncStatus = {}
    }
  },
  actions: {
    async getAll({ commit }, params) {
      try {
        const response = await axios.get('v1/dmp/panels', {
          params,
          responseType: params?.action === 'export' ? 'blob' : 'json',
        });

        if (!response.data) {
          return []
        }

        if (params?.action === 'export') {
          const contentDisposition = response.headers['content-disposition'] || '';
          const fileName = contentDisposition.match(/filename\*?=(?:UTF-8''|["']?)([^;"']+)/i)?.[1]
            || 'dmp-panels.xlsx';
          const decodedFileName = decodeURIComponent(fileName.trim());
          const url = window.URL.createObjectURL(new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', decodedFileName);
          document.body.appendChild(link);
          link.click();
          link.remove();

          store.dispatch('grid/updateDownloadNotification', {
            id: decodedFileName,
            progress: 100,
            completed: true,
            view: 'dmp-panels',
            url,
            download: decodedFileName,
            title: 'Reporte de paneles DMP',
            body: 'Descarga completa',
            error: false,
            timestamp: `${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`,
          });
          return response.data;
        }

        commit('setData', response.data.data);
        return response.data;

      } catch (error) {
        console.error('Error fetching items:', error);
        return [];
      }
    },
    async add(_, params) {
      const { data } = await axios.post('v1/dmp/panels', params)

      return data
    },
    async show(_, id) {
      const { data } = await axios.get(`v1/dmp/panels/${id}`)

      return data
    },
    async getPanelConfig({ commit }, id) {
      try {
        const { data } = await axios.get(`v1/dmp/panel-config/${id}`)

        if (data && data.success) {
          commit('setPanelConfig', data.data)
          commit('setAreasList', data.data.areas)
        }

        return data
      } catch (error) {
        console.error('Error fetching panel config:', error)
        throw error
      }
    },

    async getPanelSummary(_, id, configSection) {
      try {
        const { data } = await axios.get(`v1/dmp/panels/update-summary/id=${id}`)

        return data
      } catch (error) {
        console.error('Error fetching panel summary:', error)
        throw error
      }
    },

    async edit(_, { id, params }) {
      const { data } = await axios.put(`v1/dmp/panels/${id}`, params)

      return data
    },
    async delete(_, id) {
      const { data } = await axios.delete(`v1/dmp/panels/${id}`)

      return data
    },
    async addEntries(_, params) {
      const { data } = await axios.post('v1/dmp/panels/add-entries', params)

      return data
    },
    async copyPassword(_, params) {
      const { data } = await axios.get(`v1/dmp/panels/passwords/${params.id}`, { params: { field: params.field } })

      return data
    },
    async syncActions(_, id) {
      const { data } = await axios.post(`v1/dmp/panels/update-capabilities`, { id: id });

      return data;
    },

    async syncStatus(_, id) {
      const { data } = await axios.post(`v1/dmp/panels/update-summary`, { id: id });

      return data;
    },

    async updatePanelData(_, { id, configSection, updateDevices = false }) {
      const params = { id };
      if (configSection) {
        params.data = configSection;
      }
      if (updateDevices) {
        params.updateDevices = updateDevices;
      }

      const { data } = await axios.post(`v1/dmp/panels/update-summary`, {}, { params });

      return data;
    },

    async syncEvents(_, id) {
      const { data } = await axios.post(`v1/dmp/events/sync`, { id: id });

      return data;
    },

    async syncDevices(_, id) {
      const { data } = await axios.get(`v1/dmp/panels/${id}/sync-devices`);

      return data;
    },

    async syncPanelDevices(_, id) {
      const { data } = await axios.post('v1/dmp/panels/update-summary', {}, {
        params: {
          id,
          data: 'areas,outputs,doors,panel_status',
          updateDevices: true
        }
      });

      return data;
    },

    async syncAllPanelData(_, id) {
      const { data } = await axios.post(`v1/dmp/panels/update-summary`, {}, {
        params: {
          id,
          data: 'all',
          updateDevices: true
        }
      });

      return data;
    },

    async panelModels(_) {
      const { data } = await axios.get('v1/dmp/panels/panel-models');

      return data;
    },

    async restart(_, params) {
      const { data } = await axios.post('v1/dmp/panels/restart', params);

      return data;
    },

    async rebuild(_, params) {
      const { data } = await axios.post('v1/dmp/panels/rebuild', params);

      return data;
    },

    async reconnect(_, params) {
      const { data } = await axios.post('v1/dmp/panels/reconnect', params);

      return data;
    },

    async armPanel(_, params) {
      const { data } = await axios.get('v1/dmp/capabilities/arm-panel', { params });

      return data;
    },
    async virtualBypass(_, params) {
      const { data } = await axios.get('v1/dmp/capabilities/update-zone-status', { params });
      return data;
    },
    async getUsers(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-users', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
    },

    async setUser(_, { uuid, userData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-user', userData, {
          params: { uuid }
        });

        return data;
      } catch (error) {
        console.error('Error creating/updating user:', error);
        throw error;
      }
    },

    async deleteUser(_, { uuid, userNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-user', {
          params: { uuid },
          data: { userNumber }
        });

        return data;
      } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
      }
    },

    async getCommunicationPaths(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-communication-paths', {
          params: { uuid }
        });

        return data;
      } catch (error) {
        console.error('Error fetching communication paths:', error);
        throw error;
      }
    },

    async setCommunicationPath(_, { uuid, pathData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-communication-path', pathData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating communication path:', error);
        throw error;
      }
    },

    async deleteCommunicationPath(_, { uuid, pathNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-communication-path', {
          params: { uuid },
          data: { pathNumber }
        });

        return data;
      } catch (error) {
        console.error('Error deleting communication path:', error);
        throw error;
      }
    },

    async getOutputGroups(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-output-groups', { params: { uuid } });
        return data;
      } catch (error) {
        console.error('Error fetching output groups:', error);
        throw error;
      }
    },
    async getAreas(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-areas', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching areas:', error);
        throw error;
      }
    },

    async setOutputGroup(_, { uuid, outputGroupData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-output-group', outputGroupData, {
          params: { uuid }
        });

        return data;
      } catch (error) {
        console.error('Error creating/updating output group:', error);
        throw error;
      }
    },
    async setArea(_, { uuid, areaData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-area', areaData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating area:', error);
        throw error;
      }
    },

    async deleteOutputGroup(_, { uuid, groupNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-output-group', {
          params: { uuid },
          data: { groupNumber }
        });

        return data;
      } catch (error) {
        console.error('Error deleting output group:', error);
        throw error;
      }
    },
    async deleteArea(_, { uuid, areaNumber }) {
      console.log('Deleting area with:', { uuid, areaNumber });
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-area', {
          params: { uuid },
          data: { areaNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting area:', error);
        throw error;
      }
    },

    async setAreaGlobalSettings(_, { uuid, payload }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-global-area-config', payload, {
          params: { uuid }
        });

        return data;
      } catch (error) {
        console.error('Error saving area global settings:', error);
        throw error;
      }
    },

    async getOutputs(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-outputs', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching outputs:', error);
        throw error;
      }
    },

    async setOutput(_, { uuid, outputData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-output', outputData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating output:', error);
        throw error;
      }
    },

    async deleteOutput(_, { uuid, outputNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-output', {
          params: { uuid },
          data: { outputNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting output:', error);
        throw error;
      }
    },

    async getNetworkOptions(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-network-options', {
          params: { uuid }
        });

        return data;
      } catch (error) {
        console.error('Error fetching network options:', error);
        throw error;
      }
    },

    async setNetworkOptions(_, { uuid, options }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-network-option', options, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error setting network options:', error);
        throw error;
      }
    },

    async getOutputOptions(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-output-option', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching output options:', error);
        throw error;
      }
    },

    async setOutputOption(_, { uuid, options }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-output-option', options, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error setting output option:', error);
        throw error;
      }
    },

    async getHolidayDates(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-holiday-dates', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching holiday dates:', error);
        throw error;
      }
    },

    async setHolidayDate(_, { uuid, holidayData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-holiday-date', holidayData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating holiday date:', error);
        throw error;
      }
    },

    async deleteHolidayDate(_, { uuid, dateNumber, dates }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-holiday-date', {
          params: { uuid },
          data: { dateNumber, dates }
        });
        return data;
      } catch (error) {
        console.error('Error deleting holiday date:', error);
        throw error;
      }
    },

    async getRemoteOptions(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-remote-options', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching remote options:', error);
        throw error;
      }
    },

    async setRemoteOptions(_, { uuid, options }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-remote-option', options, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error setting remote options:', error);
        throw error;
      }
    },

    async getSystemOptions(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-system-option', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching system options:', error);
        throw error;
      }
    },

    async setSystemOptions(_, { uuid, data: systemOptionsData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-system-option', systemOptionsData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error setting system options:', error);
        throw error;
      }
    },

    async getProfiles(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-profiles', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching profiles:', error);
        throw error;
      }
    },

    async setProfile(_, { uuid, profileData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-profile', profileData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating profile:', error);
        throw error;
      }
    },

    async deleteProfile(_, { uuid, profileNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-profile', {
          params: { uuid },
          data: { profileNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting profile:', error);
        throw error;
      }
    },

    async getZone(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-zone', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching zone:', error);
        throw error;
      }
    },

    async setZone(_, { uuid, zoneData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-zone', zoneData, {
          params: { uuid }
        });
        console.log("set zones: ", data)
        return data;
      } catch (error) {
        console.error('Error creating/updating zone:', error);
        throw error;
      }
    },

    async deleteZone(_, { uuid, zoneNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-zone', {
          params: { uuid },
          data: { zoneNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting zone:', error);
        throw error;
      }
    },

    async getDeviceSetup(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-devices', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching device setup:', error);
        throw error;
      }
    },

    async setDeviceSetup(_, { uuid, deviceData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-device', deviceData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating device setup:', error);
        throw error;
      }
    },

    async deleteDeviceSetup(_, { uuid, deviceNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-device', {
          params: { uuid },
          data: { deviceNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting device setup:', error);
        throw error;
      }
    },

    async setTimeSchedule(_, { uuid, timeData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-time-schedule', timeData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating time schedule:', error);
        throw error;
      }
    },

    async getTimeSchedule(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-time-schedules', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching time schedules:', error);
        throw error;
      }
    },

    async deleteTimeSchedule(_, { uuid, scheduleNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-time-schedule', {
          params: { uuid },
          data: { scheduleNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting time schedule:', error);
        throw error;
      }
    },

    async setAreaSchedule(_, { uuid, areaScheduleData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-area-schedule', areaScheduleData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating area schedule:', error);
        throw error;
      }
    },

    async deleteAreaSchedule(_, { uuid, areaNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-area-schedule', {
          params: { uuid },
          data: { areaNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting area schedule:', error);
        throw error;
      }
    },

    async setBellOptions(_, { uuid, bellOptionsData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-bell-option', bellOptionsData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error setting bell options:', error);
        throw error;
      }
    },

    async assignDeviceTag(_, payload) {
      try {
        const { data } = await axios.post('v1/device-tag/assign', payload);
        return data;
      } catch (error) {
        console.error('Error assigning device tag:', error);
        throw error;
      }
    },

    async unassignDeviceTag(_, payload) {
      try {
        const { data } = await axios.post('v1/device-tag/unassign', payload);
        return data;
      } catch (error) {
        console.error('Error unassigning device tag:', error);
        throw error;
      }
    },
    async getCardFormats(_, uuid) {
      try {
        const { data } = await axios.get('v1/dmp/capabilities/get-card-formats', {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error fetching card formats:', error);
        throw error;
      }
    },

    async setCardFormat(_, { uuid, cardFormatData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-card-format', cardFormatData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating card format:', error);
        throw error;
      }
    },

    async deleteCardFormat(_, { uuid, formatNumber }) {
      try {
        const { data } = await axios.delete('v1/dmp/capabilities/delete-card-format', {
          params: { uuid },
          data: { formatNumber }
        });
        return data;
      } catch (error) {
        console.error('Error deleting card format:', error);
        throw error;
      }
    },
    async setSystemReport(_, { uuid, systemReportData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-system-report', systemReportData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating system report:', error);
        throw error;
      }
    },
    async setMenuDisplay(_, { uuid, menuDisplayData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-menu-display', menuDisplayData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating menu display:', error);
        throw error;
      }
    },
    async setStatusList(_, { uuid, statusListData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-status-list', statusListData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating status list:', error);
        throw error;
      }
    },
    async setPcLogReport(_, { uuid, pcLogReportData }) {
      try {
        const { data } = await axios.post('v1/dmp/capabilities/set-pclog-report', pcLogReportData, {
          params: { uuid }
        });
        return data;
      } catch (error) {
        console.error('Error creating/updating pc log report:', error);
        throw error;
      }
    },


    clear({ commit }) {
      commit('setData', [])
    },
    clearConfig({ commit }) {
      commit('clearPanelConfig')
      commit('setAreasList', [])
    }
  },
}
